import { useState, useCallback } from 'react';

export const useQuicksort = () => {
    const [isActive, setIsActive] = useState(false);
    const [currentComparison, setCurrentComparison] = useState(null);
    const [progress, setProgress] = useState({ current: 0, estimated: 0 });
    const [comparisons, setComparisons] = useState(new Map());
    
    // Internal state for the algorithm
    const [pendingResolve, setPendingResolve] = useState(null);

    // Estimate number of comparisons needed for quicksort
    const estimateComparisons = (n) => {
        if (n <= 1) return 0;
        if (n <= 3) return n - 1;  // Small arrays: just n-1 comparisons needed
        // Average case with middle-pivot optimization: ~1.1 * n * log2(n)
        return Math.ceil(1.1 * n * Math.log2(n));
    };

    // Create a comparison key for caching
    const getComparisonKey = (itemA, itemB) => {
        const keyA = itemA.id || itemA.name || JSON.stringify(itemA);
        const keyB = itemB.id || itemB.name || JSON.stringify(itemB);
        return keyA < keyB ? `${keyA}_${keyB}` : `${keyB}_${keyA}`;
    };

    // Interactive comparison function
    const compare = useCallback((itemA, itemB) => {
        return new Promise((resolve) => {
            const key = getComparisonKey(itemA, itemB);
            
            // Check if we already have this comparison
            if (comparisons.has(key)) {
                const result = comparisons.get(key);
                // Return 1 if itemA was preferred, -1 if itemB was preferred
                resolve(result.preferred === itemA ? 1 : -1);
                return;
            }

            // Need user input for this comparison
            setCurrentComparison({
                itemA,
                itemB,
                onChoice: (preferredItem) => {
                    // Cache the comparison result
                    const newComparisons = new Map(comparisons);
                    newComparisons.set(key, { preferred: preferredItem, other: preferredItem === itemA ? itemB : itemA });
                    setComparisons(newComparisons);
                    
                    // Update progress
                    setProgress(prev => ({ ...prev, current: prev.current + 1 }));
                    
                    // Clear the current comparison
                    setCurrentComparison(null);
                    
                    // Resolve with comparison result
                    resolve(preferredItem === itemA ? 1 : -1);
                }
            });
            
            // Store the resolve function to handle cancellation
            setPendingResolve(() => resolve);
        });
    }, [comparisons]);

    // Partition function for quicksort
    const partition = useCallback(async (arr, left, right) => {
        // Use middle element as pivot for better performance on sorted data
        const middleIndex = Math.floor((left + right) / 2);
        
        // Move middle element to the right position (where partition logic expects the pivot)
        if (middleIndex !== right) {
            [arr[middleIndex], arr[right]] = [arr[right], arr[middleIndex]];
        }
        
        const pivot = arr[right];
        let i = left;

        for (let j = left; j < right; j++) {
            const comparisonResult = await compare(arr[j], pivot);
            if (comparisonResult > 0) { // arr[j] is preferred over pivot
                [arr[i], arr[j]] = [arr[j], arr[i]];
                i++;
            }
        }

        [arr[i], arr[right]] = [arr[right], arr[i]];
        return i;
    }, [compare]);

    // Quicksort implementation
    const quicksortRecursive = useCallback(async (arr, left = 0, right = arr.length - 1) => {
        if (left >= right) return arr;

        const pivotIndex = await partition(arr, left, right);
        await quicksortRecursive(arr, left, pivotIndex - 1);
        await quicksortRecursive(arr, pivotIndex + 1, right);
        
        return arr;
    }, [partition]);

    // Start the sorting process
    const startSort = useCallback(async (items) => {
        if (items.length <= 1) return items;

        setIsActive(true);
        setProgress({ current: 0, estimated: estimateComparisons(items.length) });
        setComparisons(new Map());
        setCurrentComparison(null);

        try {
            // Create a copy to avoid mutating the original array
            const itemsCopy = [...items];
            const sortedItems = await quicksortRecursive(itemsCopy);
            
            // Sort completed successfully
            setIsActive(false);
            setCurrentComparison(null);
            setPendingResolve(null);
            
            return sortedItems;
        } catch (error) {
            // Handle cancellation or errors
            setIsActive(false);
            setCurrentComparison(null);
            setPendingResolve(null);
            throw error;
        }
    }, [quicksortRecursive]);

    // Cancel the sorting process
    const cancelSort = useCallback(() => {
        if (pendingResolve) {
            // Reject the pending promise to stop the algorithm
            pendingResolve(new Error('Sort cancelled'));
        }
        
        setIsActive(false);
        setCurrentComparison(null);
        setProgress({ current: 0, estimated: 0 });
        setComparisons(new Map());
        setPendingResolve(null);
    }, [pendingResolve]);

    // Make a choice in the current comparison
    const makeChoice = useCallback((preferredItem) => {
        if (currentComparison && currentComparison.onChoice) {
            currentComparison.onChoice(preferredItem);
        }
    }, [currentComparison]);

    return {
        isActive,
        currentComparison,
        progress,
        startSort,
        cancelSort,
        makeChoice
    };
};